import React, { useState, useEffect } from "react";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { DayPicker, DateRange } from "react-day-picker";
import { vi, enUS } from "date-fns/locale";
import { addDays, isBefore, isEqual, startOfDay, format, getDay } from "date-fns";
import { Button, Modal } from "../components/ui";
import SelectField from "../components/ui/SelectField";
import { AISettings } from "../api/AIContentApi";
import AIContentActions from "../actions/AIContentAction";
import { TIMEZONE_OPTIONS } from "./constants/timezoneConstants";
import { CONTENT_TYPE_OPTIONS } from "./constants/contentTypeConstants";
import PlatformSelection, { Platform } from "./components/PlatformSelection";
import ReviewDescription from "./components/ReviewDescription";
import "react-day-picker/dist/style.css";
import { selectAiSettingPlain, selectTimeSettings } from "./selectors/aiContentSelectors";
import { resolveContentInstructions } from "./constants/contentInstructionsDefaults";
import { uploadFileLogActivity } from "../api/ProjectApi";
import ImageUploadForm from "./components/ImageUploadForm";
import LayoutAction from "../actions/LayoutAction";
import AIContentAction from "../actions/AIContentAction";

const dayPickerStyles = `
  .rdp {
    --rdp-cell-size: 40px;
    --rdp-accent-color: #3b82f6;
    --rdp-background-color: #dbeafe;
    --rdp-outline: 2px solid var(--rdp-accent-color);
    --rdp-outline-selected: 2px solid rgba(0, 0, 0, 0.75);
    margin: 0;
  }
  .rdp-day_selected {
    background-color: var(--rdp-accent-color);
    color: white;
  }
  .rdp-day_selected:hover {
    background-color: var(--rdp-accent-color);
    color: white;
  }
  .rdp-day_range_start,
  .rdp-day_range_end {
    background-color: var(--rdp-accent-color);
    color: white;
  }
  .rdp-day_range_middle {
    background-color: var(--rdp-background-color);
    color: var(--rdp-accent-color);
  }
`;

export interface GeneratePayload {
  dates: Date[];
  time: { hour: number; minute: number; ampm: "AM" | "PM" };
  timezone: string;
  contentType: string;
  topic: string;
  prompt: string;
  includeWeekends: boolean;
  platforms: string[];
}

interface GenerateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (payload: GeneratePayload) => void;
  isLoading?: boolean;
  initialTopic?: string;
}

const GenerateModal: React.FC<GenerateModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  initialTopic = "",
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { projectId } = useParams<{ projectId: string }>();

  const aiSetting = useSelector(selectAiSettingPlain);
  const timeSettings = useSelector(selectTimeSettings);

  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(() => {
    const today = new Date();
    const sevenDaysLater = addDays(today, 6);
    return {
      from: today,
      to: sevenDaysLater,
    };
  });
  const [selectError, setSelectError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false);

  // Get locale for date-fns based on current language
  const getDateLocale = () => {
    const currentLang = i18n.language || "en";
    return currentLang.startsWith("vi") ? vi : enUS;
  };
  const [hour, setHour] = useState(timeSettings.hour);
  const [minute, setMinute] = useState(timeSettings.minute);
  const [ampm, setAmpm] = useState<"AM" | "PM">(timeSettings.ampm as "AM" | "PM");
  const [topic, setTopic] = useState("");
  const [contentType, setContentType] = useState("image");
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [timezone, setTimezone] = useState(timeSettings.timezone);
  const [includeWeekends, setIncludeWeekends] = useState(timeSettings.includeWeekends);
  const [defaultMediaType, setDefaultMediaType] = useState("HOST");
  const [defaultMediaId, setDefaultMediaId] = useState("");
  const [localFile, setLocalFile] = useState(null);

  // Prefill form from saved aiSetting only when modal opens (isOpen becomes true).
  // Do NOT include aiSetting or initialTopic in deps: that would re-run whenever Redux/selector
  // returns a new reference and overwrite user input (e.g. user-selected media would be reset).
  useEffect(() => {
    if (!isOpen) return;

    const topicFromSetting = aiSetting?.topic ?? "";
    setTopic(topicFromSetting || initialTopic || "");

    setHour(timeSettings.hour);
    setMinute(timeSettings.minute);
    setAmpm(timeSettings.ampm as "AM" | "PM");
    setTimezone(timeSettings.timezone);
    setIncludeWeekends(timeSettings.includeWeekends);

    if (aiSetting?.startDate && aiSetting?.endDate) {
      try {
        const from = new Date(aiSetting.startDate);
        const to = new Date(aiSetting.endDate);
        if (!isNaN(from.getTime()) && !isNaN(to.getTime())
          && from.getTime() >= Date.now()) {
          setSelectedRange({ from, to });
        }
      } catch {
        // keep current selectedRange
      }
    }

    const postTypeRaw = aiSetting?.postType;
    const postType = postTypeRaw === "singleImage" ? "image" : postTypeRaw;
    if (postType && CONTENT_TYPE_OPTIONS.some((o) => o.value === postType)) {
      setContentType(postType);
    }
    if (aiSetting?.defaultMediaId) {
      setDefaultMediaId(aiSetting.defaultMediaId);
    }
    if (aiSetting?.defaultMediaType) {
      setDefaultMediaType(aiSetting.defaultMediaType);
    }
  }, [isOpen]);

  const validateForm = () => {
    const errors: Record<string, string> = {};

    // Date range validation
    if (!selectedRange?.from) {
      errors.dateRange = t("Please select a date range");
    }

    // Timezone validation
    if (!timezone) {
      errors.timezone = t("Please select a timezone");
    }

    // Content type validation
    if (!contentType) {
      errors.contentType = t("Please select a content type");
    }

    // Image validation
    if (contentType === "image" && !defaultMediaId) {
      errors.image = t("Please upload a image");
    }

    // Platform validation
    const selectedPlatforms = platforms.filter((p) => p.selected);
    if (selectedPlatforms.length === 0) {
      errors.platforms = t("Please select at least one platform");
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onConfirm = async () => {
    if (!validateForm()) {
      return;
    }
    if (!projectId) {
      console.error("Project ID is required");
      return;
    }
    if (localFile) {
      await uploadFileLogActivity("", localFile, "Shared");
      dispatch(AIContentAction.getFileImages(projectId));
    }
    setIsOpenConfirm(true);
  };

  const handleConfirmDialog = async () => {
    if (!projectId) {
      console.error("Project ID is required");
      return;
    }
    const selectedPlatforms = platforms.filter((p) => p.selected);

    // Generate dates array from range using date-fns
    const dates: Date[] = [];
    const startDate = startOfDay(selectedRange!.from);
    const endDate = selectedRange!.to ? startOfDay(selectedRange!.to) : startDate;

    let current = startDate;
    while (isBefore(current, endDate) || isEqual(current, endDate)) {
      const day = getDay(current);
      const isWeekendDay = day === 0 || day === 6;
      if (includeWeekends || !isWeekendDay) {
        dates.push(new Date(current));
      }
      current = addDays(current, 1);
    }

    try {
      if (contentType === "image") {
        if (!defaultMediaId || !defaultMediaType) {
          dispatch(
            LayoutAction.setNotification({
              title: t("Image"),
              type: "warning",
              message: t("Image is required"),
            })
          );
          return;
        }
      }
      if (!topic) {
        dispatch(
          LayoutAction.setNotification({
            title: t("Topic"),
            type: "warning",
            message: t("Topic is required"),
          })
        );
        return;
      }
      // Prepare AI settings for the API call (include agent fields so we don't overwrite saved custom agent settings)
      const aiSettings: AISettings = {
        serviceDescription: aiSetting?.serviceDescription || "",
        customerLocation: aiSetting?.customerLocation || "",
        companyIndustry: aiSetting?.companyIndustry || "",
        topic: topic,
        customPrompt: aiSetting?.customPrompt || "",
        language: aiSetting?.language || "en",
        timezone: timezone,
        contentVariation: "medium",
        startTime: (() => {
          let hour24 = hour;
          if (ampm === "PM" && hour !== 12) {
            hour24 = hour + 12;
          } else if (ampm === "AM" && hour === 12) {
            hour24 = 0;
          }
          return `${hour24.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
        })(),
        startDate: format(startDate, "yyyy-MM-dd"),
        endDate: format(endDate, "yyyy-MM-dd"),
        totalDays: dates.length,
        includeWeekends: includeWeekends,
        postType: contentType,
        defaultMediaId: defaultMediaId,
        defaultMediaType: defaultMediaType,
        facebook: aiSetting?.facebook || false,
        twitter: aiSetting?.twitter || false,
        instagram: aiSetting?.instagram || false,
        linkedin: aiSetting?.linkedin || false,
        threads: aiSetting?.threads || false,
        contentInstructions: resolveContentInstructions(aiSetting),
      };

      // Save AI content settings using Redux action
      await dispatch(AIContentActions.saveAIContentSetting(projectId, aiSettings));

      // Call the original onGenerate callback
      onGenerate({
        dates,
        time: { hour, minute, ampm },
        timezone,
        contentType,
        topic,
        prompt: aiSetting?.customPrompt || "",
        includeWeekends,
        platforms: selectedPlatforms.map((p) => p.id),
      });
    } catch (error) {
      console.error("Error saving AI content settings:", error);
      // Still call onGenerate even if settings save fails
      onGenerate({
        dates,
        time: { hour, minute, ampm },
        timezone,
        contentType,
        topic,
        prompt: aiSetting?.customPrompt || "",
        includeWeekends,
        platforms: selectedPlatforms.map((p) => p.id),
      });
    }
    setIsOpenConfirm(false);
  };

  const handleChangeImage = (type, fileId, file) => {
    setDefaultMediaType(type);
    setDefaultMediaId(fileId);
    setLocalFile(file);
  };

  const modalContent = (
    <div className="px-6 py-6 space-y-8">
      <style>{dayPickerStyles}</style>

      {/* Schedule Section */}
      <div className="space-y-4">
        {/* Question outside the card */}
        <div className="text-base font-semibold text-gray-900">
          {t("When do you want to publish new content?")} <span className="text-red-500">*</span>
        </div>

        {/* Include Weekends Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="includeWeekends"
              checked={includeWeekends}
              onChange={(e) => setIncludeWeekends(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="includeWeekends" className="text-sm text-gray-700">
              {t("Post on weekend (Saturday, Sunday)?")}
            </label>
          </div>
        </div>

        {/* Card container */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
          {/* Calendar */}
          <div className="p-4 flex justify-center">
            <DayPicker
              mode="range"
              selected={selectedRange}
              onSelect={(range) => {
                if (!range) {
                  setSelectedRange(undefined);
                  setSelectError(null);
                  return;
                }

                // Check if range exceeds 7 days
                if (range.from && range.to) {
                  const daysDiff =
                    Math.ceil((range.to.getTime() - range.from.getTime()) / (1000 * 60 * 60 * 24)) +
                    1;
                  if (daysDiff > 7) {
                    setSelectError(t("You can select maximum 7 days"));
                    // Limit to 7 days from start date using date-fns
                    const limitedEnd = addDays(range.from, 6);
                    setSelectedRange({
                      from: range.from,
                      to: limitedEnd,
                    });
                  } else {
                    setSelectError(null);
                    setSelectedRange(range);
                  }
                } else {
                  setSelectError(null);
                  setSelectedRange(range);
                }
              }}
              onMonthChange={() => {
                setSelectedRange(undefined);
                setSelectError(null);
              }}
              locale={getDateLocale()}
              className="w-auto"
              numberOfMonths={1}
              weekStartsOn={1}
              showOutsideDays
              fixedWeeks
              disabled={(date) => {
                const today = startOfDay(new Date());
                const past = isBefore(date, today);
                const weekend = !includeWeekends && (getDay(date) === 0 || getDay(date) === 6);
                return past || weekend;
              }}
              modifiers={{
                disabled: (date) => {
                  const today = startOfDay(new Date());
                  const past = isBefore(date, today);
                  const weekend = !includeWeekends && (getDay(date) === 0 || getDay(date) === 6);
                  return past || weekend;
                },
              }}
              modifiersStyles={{
                disabled: {
                  color: "#9CA3AF",
                  backgroundColor: "#F3F4F6",
                  opacity: 0.5,
                  cursor: "not-allowed",
                },
              }}
            />
          </div>

          {/* Timezone and Time Selection */}
          <div className="px-4 pb-4 border-t border-gray-200">
            <div className="pt-4 flex justify-center">
              <div className="flex flex-col items-center gap-2">
                {/* Timezone */}
                <div className="w-58">
                  <SelectField
                    value={{
                      value: timezone,
                      label:
                        TIMEZONE_OPTIONS.find((opt) => opt.value === timezone)?.label || timezone,
                    }}
                    onChange={(option: any) => {
                      setTimezone(option?.value);
                      // Clear validation error when user selects
                      if (validationErrors.timezone) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          timezone: "",
                        }));
                      }
                    }}
                    options={TIMEZONE_OPTIONS}
                    placeholder={t("Select timezone")}
                    menuPortalTarget={null}
                    menuPosition="fixed"
                    errorMessage={validationErrors.timezone}
                  />
                </div>

                {/* Time Selection */}
                <div className="flex items-center gap-2">
                  <div className="w-20">
                    <SelectField
                      value={{
                        value: hour,
                        label: String(hour).padStart(2, "0"),
                      }}
                      onChange={(option: any) => setHour(Number(option?.value))}
                      options={Array.from({ length: 12 }, (_, i) => ({
                        value: i + 1,
                        label: String(i + 1).padStart(2, "0"),
                      }))}
                      placeholder="09"
                      menuPortalTarget={null}
                      menuPosition="fixed"
                      isClearable={false}
                      isSearchable={false}
                    />
                  </div>
                  <span className="text-gray-500">:</span>
                  <div className="w-20">
                    <SelectField
                      value={{
                        value: minute,
                        label: String(minute).padStart(2, "0"),
                      }}
                      onChange={(option: any) => setMinute(Number(option?.value))}
                      options={[0, 15, 30, 45].map((m) => ({
                        value: m,
                        label: String(m).padStart(2, "0"),
                      }))}
                      placeholder="30"
                      menuPortalTarget={null}
                      menuPosition="fixed"
                      isClearable={false}
                      isSearchable={false}
                    />
                  </div>
                  <div className="w-20">
                    <SelectField
                      value={{
                        value: ampm,
                        label: ampm,
                      }}
                      onChange={(option: any) => setAmpm(option?.value)}
                      options={[
                        { value: "AM", label: "AM" },
                        { value: "PM", label: "PM" },
                      ]}
                      placeholder="AM"
                      menuPortalTarget={null}
                      menuPosition="fixed"
                      isClearable={false}
                      isSearchable={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error messages */}
          <div className="px-4 pb-4 space-y-1">
            {validationErrors.dateRange && (
              <div className="text-xs text-red-500 text-center">{validationErrors.dateRange}</div>
            )}
            <div className={`text-right text-xs ${selectError ? "text-red-500" : "text-gray-500"}`}>
              {selectError || t("You can select maximum 7 days")}
            </div>
          </div>
        </div>
      </div>

      {/* Content Type Section */}
      <div className="space-y-4">
        <SelectField
          label={`${t("What type of content do you want to create?")} *`}
          value={{
            value: contentType,
            label:
              CONTENT_TYPE_OPTIONS.find((option) => option.value === contentType)?.label ||
              contentType,
          }}
          onChange={(option: any) => {
            setContentType(option?.value);
            // Clear validation error when user selects
            if (validationErrors.contentType) {
              setValidationErrors((prev) => ({ ...prev, contentType: "" }));
            }
          }}
          options={CONTENT_TYPE_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
            isDisabled: !option.supported,
            supported: option.supported,
          }))}
          placeholder={t("Select content type")}
          menuPortalTarget={null}
          menuPosition="fixed"
          errorMessage={validationErrors.contentType}
          formatOptionLabel={(option: any, { context }) => {
            if (context === "value") {
              return (
                <div className="flex items-center justify-between w-full">
                  <span>{option.label}</span>
                  {option.supported === false && (
                    <span className="text-xs text-red-500 ml-2">(Unsupported)</span>
                  )}
                </div>
              );
            }
            return (
              <div className="flex items-center justify-between w-full">
                <span>{option.label}</span>
                {option.supported === false && (
                  <span className="text-xs text-red-500 ml-2">(Unsupported)</span>
                )}
              </div>
            );
          }}
        />
        {contentType === "image" ? (
          <div className="w-full">
            <div className="text-sm text-gray-500 mb-3">{t("Use image up to 3MB")}</div>
            <ImageUploadForm onChange={handleChangeImage} />
            {validationErrors.image && (
              <div className="text-xs text-red-500 text-center">{validationErrors.image}</div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500 mt-1">
            {CONTENT_TYPE_OPTIONS.find((option) => option.value === contentType)?.description}
          </p>
        )}
      </div>

      {/* Platform Selection Section */}
      <div className="space-y-4">
        <div className="text-base font-semibold text-gray-900">
          {t("Select platforms")} <span className="text-red-500">*</span>
        </div>
        <PlatformSelection
          view="generate-modal"
          onPlatformsChange={(platforms) => {
            setPlatforms(platforms);
          }}
        />
        {validationErrors.platforms && (
          <div className="text-xs text-red-500">{validationErrors.platforms}</div>
        )}
      </div>
    </div>
  );

  const modalFooter = (
    <div className="flex items-center justify-end gap-3">
      <Button variant="secondary" onClick={onClose}>
        {t("Cancel")}
      </Button>
      <Button variant="primary" onClick={onConfirm} leftIcon={<Sparkles className="w-4 h-4" />}>
        {t("Generate Posts")}
      </Button>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t("Generate AI Content")}
        subtitle={t("Please select your schedule and platforms")}
        position="right"
        footer={modalFooter}
        className="h-full w-[531px]"
      >
        {modalContent}
      </Modal>

      <Modal
        isOpen={isOpenConfirm}
        onClose={() => {
          setIsOpenConfirm(false);
        }}
        title={t("Confirm Generation")}
        footer={
          <div className="flex items-center justify-end gap-3">
            <Button variant="secondary" onClick={() => setIsOpenConfirm(false)}>
              {t("Cancel")}
            </Button>
            <Button variant="primary" onClick={handleConfirmDialog}>
              {t("Confirm")}
            </Button>
          </div>
        }
        className="w-[600px] max-h-[98vh]"
      >
        <ReviewDescription
          topic={topic}
          onChangeTopic={(value) => setTopic(value)}
          aiSetting={aiSetting}
          selectedRange={selectedRange}
          hour={hour}
          minute={minute}
          ampm={ampm}
          timezone={timezone}
          contentType={contentType}
          defaultMediaId={defaultMediaId}
          defaultMediaType={defaultMediaType}
          platforms={platforms}
          includeWeekends={includeWeekends}
        />
      </Modal>
    </>
  );
};

export default GenerateModal;
